/* eslint-disable react/jsx-key */
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { NextImage, RichText, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { StHTMLDescription } from './SearchTrialItem.style';
import useResultList from 'lib/hooks/coveo/useResultList';
import useNearestSite from 'lib/hooks/coveo/useNearestSite';
import useGeoLocation from 'lib/hooks/useGeoLocation';
import headlessEngine from 'lib/utils/coveo/headlessEngine';
import { highlightExcerpt, sortByGoodMatch } from 'lib/utils/coveoHeadless';
import { AppDispatch, RootState } from '../../store/store';
import Pagination from './Pagination';
import {
  fetchProfileDetailsForCR,
  updateSavedTrialsForMyProfile,
  updateSavedTrialsofCRRegUser,
} from 'lib/utils/axiosCDPFetcher';
import CRModal from './CRModal';
import Favorite from '@mui/icons-material/Favorite';
import { hideLoader, showLoader } from 'src/features/loaderSlice';
import { getPager, setPager } from 'lib/utils/coveo/headlessPager';
import { buildInteractiveResult, Result } from '@coveo/headless';
import useQuerySummary from 'lib/hooks/coveo/useQuerySummary';
import { mergeTag, scrollToSectionPath } from '../../lib/utils/common';
import getAgeRange from '../../lib/utils/coveo/getAgeRange';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import UiText from '../shared/Typography/UiText';
import { JSSLink } from '../shared/Links/JSSLink.styles';
import ButtonDiscreet from '../shared/Button/ButtonDiscreet';
import UiBox from '../shared/Box';
import Tag from '../shared/Tags/Tag';
import { useI18n } from 'next-localization';
import { getStatusTags } from '../../lib/utils/coveo/fetchTrialStatusTags';

const KeyMapper: any = {
  Age: 'trialminage',
  Phase: 'trialphase',
  'Participants needed': 'trialenrollment',
  'Est. Completion Date': 'trialcompletiondate',
};

export const StDescriptopn = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'limit',
})<any>(() => ({
  lineHeight: '1.95ch',
}));

const getmModalCTAButtonByName = (buttons: any, name: string): any => {
  if (buttons?.length > 0) {
    const item = buttons.find((item: any) => item?.name === name);
    if (item) {
      return {
        buttonVariant: item?.fields?.buttonVariant?.value,
        value: item?.fields?.link?.value?.text,
        anchor: item?.fields?.link,
        name: item?.displayName,
      };
    } else {
      return null;
    }
  }
};

const SearchTrialItems = (props: any): JSX.Element => {
  const theme: any = useTheme();
  const isLaptopMode = useMediaQuery(theme.breakpoints.down('lg'));
  const showFullCard = useMediaQuery(theme.breakpoints.between(960, 1250));
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down(960));
  const [savedTrials, setSavedTrials] = useState([]);
  const [stepToPagination, setStepToPagination] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isShowToolTipModal, setIsShowToolTipModal] = useState(false);
  const { location } = useGeoLocation();
  const selectedTrial = useRef<any>(null);
  const { zipCode, isVirtual, dayOfBirth, distance } = useSelector(
    (state: RootState) => state.coveoResult
  );
  const isAuthenticated: boolean = useSelector(
    (state: RootState) => state.authentication.isAuthenticated
  );
  const dispatch = useDispatch<AppDispatch>();
  const totalResults = useQuerySummary();
  const [updatedModalDescription, setUpdatedModalDescription] = useState({ value: '' });
  //Pagination
  const MAX_PAGINATION_STEPS = props?.fields?.viewMoreCount?.value || 2;
  const AMOUNT_TO_PAGINATION =
    props?.fields?.noOfRecordsPerPage?.value * (props?.fields?.viewMoreCount?.value + 1) || 18;
  const GOOD_MATCH_DISTANCE = props?.fields?.goodMatchDistance?.value || 25;
  const noResultsText = isAuthenticated
    ? props?.fields?.noResultsLoggeninText || { value: 'No Results' }
    : props?.fields?.noResultsText || { value: 'No Results' };
  const [resultsPerPage, setResultsPerPage] = useState<number>(
    props?.fields?.noOfRecordsPerPage?.value || 6
  );

  const { state, fetchMoreResults } = useResultList(
    headlessEngine,
    zipCode,
    dayOfBirth,
    location,
    resultsPerPage
  );
  const getNearestSite = useNearestSite(state, location);
  const lineCount = props?.fields?.lineCount?.value || 3;
  const { sitecoreContext } = useSitecoreContext();

  //TODO: need to check this logic for view more cards
  //const pager = useMemo(() => getPager(props?.fields?.noOfRecordsPerPage?.value || 6), []);
  const pager = useMemo(() => getPager(resultsPerPage), []);
  const { fontWeight, fontSize, tagFontWeight, valueFontColor } = props?.fields;

  const [noResultsTextDelay, setNoResultsTextDelay] = useState<boolean>(false);

  const {
    seeIfYouQualifyButton,
    viewDetailsButton,
    modalCTAButtons,
    modalTitle,
    modalDescription,
    favoriteButtonBorderColor,
    favoriteButtonColor,
    favoriteButtonToolTipBGColor,
  } = props?.fields;
  const cancel = getmModalCTAButtonByName(modalCTAButtons, 'Cancel');
  const continueBtn = getmModalCTAButtonByName(modalCTAButtons, 'Continue');
  const { t } = useI18n();
  const [fieldsData] = useState(() => {
    const fieldsData: any = [];
    props?.fields?.searchTrialFields?.forEach((item: any) => {
      if (item?.fields?.key?.value && item?.fields?.icon?.value && item?.fields?.label?.value) {
        fieldsData.push({
          label: item?.fields?.label,
          icon: item?.fields?.icon,
          key: item?.fields?.key,
        });
      }
    });
    return fieldsData;
  });

  const onCloseModal = () => {
    setOpenModal(false);
  };

  const onCancelModal = () => {
    setOpenModal(false);
  };

  const ShowTooltipDetailClickHandler = (e: any) => {
    e.preventDefault();
    setIsShowToolTipModal(isTabletOrMobile);
  };

  const handleActionCTAClick = (event: any) => {
    event?.preventDefault();
    const id = event?.target?.id;
    if (id === 'Register') {
      scrollToSectionPath('#cr-registration', 0, 500);
      setIsShowToolTipModal(false);
    } else {
      setIsShowToolTipModal(false);
    }
  };

  const getRelativeHref = (fullUrl: string): string => {
    if (typeof window === 'undefined') return fullUrl; // SSR-safe fallback

    try {
      const { origin } = window.location;
      return fullUrl?.startsWith(origin) ? fullUrl?.replace(origin, '') : fullUrl;
    } catch {
      return '#';
    }
  };

  const ShowTooltipDetailModal = () => {
    if (isTabletOrMobile) {
      return (
        <CRModal
          open={isShowToolTipModal}
          handleClose={() => setIsShowToolTipModal(false)}
          handleActionClick={handleActionCTAClick}
          title={props?.fields?.saveModalTitle?.value}
          ctaButtons={props?.fields?.saveModalCTAButtons}
        >
          <Box sx={{ color: (theme: any) => theme.palette.neutral8.dark }}>
            <Text field={props?.fields?.saveModalDescription} />
          </Box>
        </CRModal>
      );
    } else {
      return <></>;
    }
  };

  const handleSaveAndUnsaveTrial = async (isSaved: boolean) => {
    const payload: any = {
      myProfileToken: sessionStorage.getItem('myProfileVerificationToken'),
      trialId: selectedTrial?.current,
      isSaved: isSaved ? true : false,
    };
    try {
      dispatch(showLoader());
      await updateSavedTrialsForMyProfile(payload);
      await getSetProfileData();
      if (process.env.NEXT_PUBLIC_ALLOW_REGISTRATION_API_CALL === 'true') {
        await updateSavedTrialsofCRRegUser(payload);
      }
      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
    }
  };
  const onContinueModal = async () => {
    // api call to be done
    if (!selectedTrial.current) {
      return;
    }
    setOpenModal(false);
    await handleSaveAndUnsaveTrial(false);
    selectedTrial.current = null;
  };

  const getSetProfileData = async () => {
    const myProfileVerificationToken = sessionStorage.getItem('myProfileVerificationToken');
    if (myProfileVerificationToken) {
      try {
        const { data } = await fetchProfileDetailsForCR(myProfileVerificationToken);
        setSavedTrials(data?.trialIds);
      } catch (_e) {}
    }
  };

  useEffect(() => {
    dispatch(showLoader());
    setTimeout(() => {
      setNoResultsTextDelay(true);
    }, 3000);

    if (noResultsTextDelay) {
      dispatch(hideLoader());
    }
  }, [noResultsTextDelay]);

  useEffect(() => {
    const fetchMyProfile = async () => {
      if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
        return;
      }
      await getSetProfileData();
    };
    fetchMyProfile();
  }, []);

  const isGoodMatchByDistance = useCallback(
    (item: any) => {
      const nearestSite = getNearestSite(item);
      return parseFloat(nearestSite as string) <= GOOD_MATCH_DISTANCE ? true : false;
    },
    [getNearestSite]
  );

  const filteredAndSortedResults = useMemo(() => {
    if (!state?.results || !state?.results.length) {
      return [];
    }

    const featuredtrials = [...state.results].filter((item) => item.raw?.isfeaturedtrial === '1');
    const otherTrials = [...state.results].filter((item) => item.raw?.isfeaturedtrial !== '1');

    const featuredSortedByGoodMatch = featuredtrials.sort((a, b) =>
      sortByGoodMatch(a, b, isGoodMatchByDistance)
    );

    const sortedTrials = featuredtrials.length
      ? [...featuredSortedByGoodMatch, ...otherTrials]
      : [...state.results].sort((a, b) => sortByGoodMatch(a, b, isGoodMatchByDistance));

    if (isVirtual) {
      return sortedTrials.filter((item: any) => item.raw.isvirtual);
    } else {
      return sortedTrials;
    }
  }, [isGoodMatchByDistance, isVirtual, state?.results, distance]);

  const handleFetchMoreResults = () => {
    if (stepToPagination < MAX_PAGINATION_STEPS) {
      setStepToPagination((prev) => prev + 1);
    }
    fetchMoreResults();
  };

  const handleOnClick = async (data: any, isSave: boolean, title = '') => {
    const updatedText = mergeTag(modalDescription?.value, {
      TrialStudyName: title,
    });
    setUpdatedModalDescription({ value: updatedText });
    if (!isTokenAvailable()) {
      return;
    }
    selectedTrial.current = data?.trialname;
    if (isSave === false) {
      setOpenModal(true);
    } else {
      await handleSaveAndUnsaveTrial(true);
    }
  };

  const onCardLinkClick = (item: Result) => () => {
    const interactiveResultController = buildInteractiveResult(headlessEngine, {
      options: { result: item },
    });
    interactiveResultController.select();
  };

  useEffect(() => {
    if (stepToPagination === MAX_PAGINATION_STEPS) {
      setResultsPerPage(AMOUNT_TO_PAGINATION);
    } else {
      setResultsPerPage(
        stepToPagination * props?.fields?.noOfRecordsPerPage?.value +
          props?.fields?.noOfRecordsPerPage?.value
      );
    }
  }, [stepToPagination]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    setPager(pager);
  }, [totalResults]);

  const isTokenAvailable = () => {
    return sessionStorage.getItem('myProfileVerificationToken') ? true : false;
  };

  useEffect(() => {
    if (!isTabletOrMobile) {
      setIsShowToolTipModal(false);
    }
  }, [isTabletOrMobile]);

  const generateTrialSchema = () => {
    if (!filteredAndSortedResults || filteredAndSortedResults.length === 0) {
      return null;
    }

    const trialSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: filteredAndSortedResults.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: item?.clickUri,
      })),
    };

    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(trialSchema),
        }}
      />
    );
  };

  return (
    <Grid item container>
      <Grid
        item
        container
        xs={12}
        sx={{
          padding: '16px',
          boxSizing: 'border-box',
          flexWrap: 'wrap',
          alignContent: 'flex-start',
          background:
            isTabletOrMobile && filteredAndSortedResults.length > 0
              ? (theme.backgroundGradients?.backgroundGradientColor6 ?? '')
              : 'none',
        }}
        columnSpacing={1.5}
        rowSpacing={2}
      >
        {generateTrialSchema()}
        {filteredAndSortedResults.length > 0
          ? filteredAndSortedResults.map((item: any, index) => {
              return (
                <Grid
                  item
                  container
                  xs={12}
                  md={12}
                  lg={6}
                  key={item.UniqueId}
                  sx={{
                    minWidth: showFullCard ? '100%' : '330px',
                    height: 'fit-content',
                  }}
                >
                  <Box
                    sx={{
                      minWidth: showFullCard ? '100%' : '330px',
                      width: '100%',
                      height: 'fit-content',
                    }}
                  >
                    <Box
                      id={'ui-trial-card-' + index}
                      className="ui-trial-card"
                      sx={{
                        padding: '24px',
                        borderRadius: '16px',
                        background: '#FFF',
                        boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
                        width: '100%',
                      }}
                    >
                      <Grid
                        item
                        container
                        sx={{
                          flexDirection: 'row',
                          marginBottom: '24px',
                        }}
                      >
                        {getStatusTags(
                          item,
                          isGoodMatchByDistance(item),
                          t('goodMatch'),
                          t('virtual'),
                          t('enrolling'),
                          t('noYetEnrolling'),
                          props?.fields,
                          '#595959'
                        ).map((tag, index) => {
                          return (
                            <Tag
                              color={tag?.color}
                              index={index}
                              tag={tag?.label}
                              fontSize={fontSize?.value || '13px'}
                              fontWeight={tagFontWeight?.value || 500}
                              key={index}
                            />
                          );
                        })}

                        <Grid
                          item
                          flex={1}
                          textAlign={'right'}
                          alignSelf={'center'}
                          onClick={(e: any) =>
                            !isTokenAvailable()
                              ? ShowTooltipDetailClickHandler(e)
                              : e?.preventDefault()
                          }
                        >
                          <Tooltip
                            id={'tooltip-favorite'}
                            className={'ui-tooltip-favorite'}
                            title={
                              <RichText
                                field={
                                  !isTokenAvailable()
                                    ? props?.fields?.saveButtonHoverText
                                    : savedTrials &&
                                        (savedTrials as any)?.indexOf(
                                          item?.raw?.trialname as string
                                        ) > -1
                                      ? props?.fields?.unSaveButtonHoverTextLoggedIn
                                      : props?.fields?.saveButtonHoverTextLoggenIn
                                }
                              />
                            }
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: favoriteButtonToolTipBGColor?.value,
                                  border: `1px solid ${favoriteButtonBorderColor?.value}`,
                                  borderRadius: '12px',
                                  boxShadow: 3,
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 'none',
                                },
                              },
                              arrow: {
                                sx: {
                                  color: favoriteButtonToolTipBGColor?.value, // Arrow color should match tooltip background
                                },
                              },
                            }}
                            arrow
                          >
                            {savedTrials &&
                            (savedTrials as any)?.indexOf(item?.raw?.trialname as string) > -1 ? (
                              <Favorite
                                id={'favorite-icon'}
                                sx={{
                                  cursor: 'pointer',
                                  color: favoriteButtonColor?.value || 'black',
                                }}
                                onClick={() => handleOnClick(item.raw, false, item.title)}
                              />
                            ) : (
                              <FavoriteBorderIcon
                                id={'favorite-border-icon'}
                                sx={{
                                  cursor: 'pointer',
                                  color: favoriteButtonBorderColor?.value || 'black',
                                }}
                                onClick={() => handleOnClick(item.raw, true, item.title)}
                              />
                            )}
                          </Tooltip>
                        </Grid>
                      </Grid>
                      <StHTMLDescription item>
                        <UiText
                          className="trial-card-title"
                          color="default"
                          trimRowCount={lineCount}
                          styles={{ fontSize: '22px' }}
                        >
                          <StDescriptopn
                            dangerouslySetInnerHTML={{
                              __html: highlightExcerpt(item),
                            }}
                          />
                        </UiText>
                      </StHTMLDescription>
                      {fieldsData.map((field: any, index: number) => (
                        <Grid
                          key={index}
                          item
                          container
                          flexDirection={'row'}
                          justifyContent={'space-between'}
                          mb={3}
                        >
                          <NextImage field={field.icon} />
                          <Grid
                            item
                            ml={2}
                            fontWeight={fontWeight?.value || '600'}
                            fontSize={fontSize?.value || '16px'}
                            sx={{ color: (theme: any) => theme.palette.hintText.main }}
                          >
                            {field.label.value}
                          </Grid>
                          <Grid
                            item
                            flex={1}
                            textAlign={'right'}
                            sx={{
                              ...(isLaptopMode && { marginLeft: 2 }),
                              color: (theme: any) =>
                                valueFontColor?.value || theme.palette.common.black,
                            }}
                          >
                            {field?.key?.value === 'Nearest Site'
                              ? getNearestSite(item) || <span>&#8212;</span>
                              : field?.key?.value === 'Age'
                                ? getAgeRange(item?.raw?.trialminage, item?.raw?.trialmaxage) || (
                                    <span>&#8212;</span>
                                  )
                                : item?.raw?.[KeyMapper[field.key.value]]}
                          </Grid>
                        </Grid>
                      ))}

                      <Grid
                        item
                        container
                        spacing={1.5}
                        sx={{
                          boxSizing: 'border-box',
                        }}
                      >
                        {item.clickUri && (
                          <Grid
                            item
                            xs={item.raw.featuredeprurl ? 6 : 12}
                            lg={item.raw.featuredeprurl ? 6 : 12}
                          >
                            <JSSLink
                              sx={{ width: '100%', minWidth: 'initial', cursor: 'pointer' }}
                              id={''}
                              className={''}
                              key={''}
                              onClick={() => onCardLinkClick(item)()} // Pass event
                              variant="outlined"
                              field={{
                                value: {
                                  href: getRelativeHref(item?.clickUri),
                                  text: viewDetailsButton?.value || 'View Details',
                                },
                              }}
                              aria-label="text"
                            />
                          </Grid>
                        )}
                        {item.raw.featuredeprurl && (
                          <Grid item xs={6} lg={6}>
                            <JSSLink
                              sx={{ width: '100%', minWidth: 'initial', cursor: 'pointer' }}
                              id={''}
                              className={''}
                              key={''}
                              onClick={() => onCardLinkClick(item)()} // Pass event
                              variant="contained"
                              field={{
                                value: {
                                  href: item.raw?.featuredeprurl || '#',
                                  text: seeIfYouQualifyButton?.value || 'See if you qualify',
                                },
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Box>
                </Grid>
              );
            })
          : state?.isLoading !== undefined &&
            !state?.isLoading &&
            filteredAndSortedResults.length === 0 &&
            noResultsTextDelay && (
              <Grid
                id="no-result"
                sx={{
                  marginLeft: '1rem',
                  fontSize: '18px',
                  marginTop: '2rem',
                  marginBottom: '20px',
                  color: theme.palette.neutral8.main,
                }}
              >
                <UiText color="default">
                  <RichText field={noResultsText} />
                </UiText>
              </Grid>
            )}

        {state?.moreResultsAvailable &&
        stepToPagination < MAX_PAGINATION_STEPS &&
        filteredAndSortedResults.length ? (
          <UiBox styles={{ margin: '0 auto', paddingTop: '20px' }}>
            <ButtonDiscreet onClick={handleFetchMoreResults}>
              <Text field={props?.fields?.viewMoreText}></Text>
            </ButtonDiscreet>
          </UiBox>
        ) : null}

        <CRModal
          open={openModal}
          handleClose={onCloseModal}
          handleCancel={onCancelModal}
          handleContinue={onContinueModal}
          title={modalTitle?.value}
          confirmButton={continueBtn}
          cancelButton={cancel}
        >
          <RichText field={updatedModalDescription} />
        </CRModal>
        <ShowTooltipDetailModal />
      </Grid>
      {stepToPagination === MAX_PAGINATION_STEPS &&
        filteredAndSortedResults.length &&
        totalResults > AMOUNT_TO_PAGINATION && <Pagination pager={pager} />}
    </Grid>
  );
};

export default SearchTrialItems;
